// src/components/auth/Register.jsx
import React, { useState, useContext } from 'react';
import {
    Container, Box, Typography, TextField, Button,
    Paper, Avatar, CircularProgress, Alert, Link, IconButton
} from '@mui/material';
import { PersonAddOutlined, Security as SecurityIcon, Home as HomeIcon } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AuthContext } from '../../contexts/AuthContext';

const validationSchema = Yup.object({
    full_name: Yup.string()
        .required('Full name is required'),
    email: Yup.string()
        .email('Enter a valid email')
        .required('Email is required'),
    password: Yup.string()
        .min(8, 'Password should be at least 8 characters')
        .required('Password is required'),
    confirm_password: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Confirm your password')
});

const Register = () => {
    const [error, setError] = useState(null);
    const { register, loading } = useContext(AuthContext);
    const navigate = useNavigate();

    const formik = useFormik({
        initialValues: {
            full_name: '',
            email: '',
            password: '',
            confirm_password: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                setError(null);
                // Remove confirm_password before sending to API
                const { confirm_password, ...userData } = values;
                await register(userData);
                navigate('/dashboard');
            } catch (err) {
                setError(err.message || 'Registration failed. Please try again.');
            }
        }
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: { xs: '100vh', sm: 'calc(100vh - 128px)' },
                width: '100%',
                px: { xs: 2, sm: 0 },
                py: { xs: 2, sm: 0 }
            }}
        >
            <Container maxWidth="xs">
                <Paper
                    elevation={3}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: { xs: 3, sm: 4 },
                        borderRadius: 2,
                        position: 'relative'
                    }}
                >
                    {/* Home Button */}
                    <IconButton
                        component={RouterLink}
                        to="/"
                        sx={{
                            position: 'absolute',
                            top: { xs: 8, sm: 16 },
                            left: { xs: 8, sm: 16 },
                            color: 'text.secondary',
                            '&:hover': {
                                color: 'secondary.main',
                                bgcolor: 'secondary.light',
                            }
                        }}
                        size="small"
                        aria-label="Go to home page"
                    >
                        <HomeIcon />
                    </IconButton>

                    {/* Logo */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: { xs: 2, sm: 0 } }}>
                        <SecurityIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1 }} />
                        <Typography variant="h4" component="div" color="primary.main" fontWeight="bold">
                            RiskAS
                        </Typography>
                    </Box>

                    <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                        <PersonAddOutlined />
                    </Avatar>

                    <Typography component="h1" variant="h5">
                        Create Account
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 3, width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="full_name"
                            label="Full Name"
                            name="full_name"
                            autoComplete="name"
                            autoFocus
                            value={formik.values.full_name}
                            onChange={formik.handleChange}
                            error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                            helperText={formik.touched.full_name && formik.errors.full_name}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="new-password"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            error={formik.touched.password && Boolean(formik.errors.password)}
                            helperText={formik.touched.password && formik.errors.password}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirm_password"
                            label="Confirm Password"
                            type="password"
                            id="confirm_password"
                            autoComplete="new-password"
                            value={formik.values.confirm_password}
                            onChange={formik.handleChange}
                            error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
                            helperText={formik.touched.confirm_password && formik.errors.confirm_password}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="secondary"
                            sx={{ mt: 3, mb: 2, py: 1.2 }}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : null}
                        >
                            {loading ? 'Creating Account...' : 'Create Account'}
                        </Button>

                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: { xs: 1, sm: 0 },
                            mt: 2
                        }}>
                            <Link component={RouterLink} to="/login" variant="body2">
                                {"Already have an account? Sign In"}
                            </Link>
                            <Link
                                component={RouterLink}
                                to="/"
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': { color: 'secondary.main' }
                                }}
                            >
                                Back to Home
                            </Link>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default Register;